/*
  Where this deployment's identity provider and API live, resolved at RUN time.

  `import.meta.env.VITE_*` is substituted into the bundle at build time. That would
  mean a separate build per environment, and the thing that ships would no longer be
  the thing that was tested. So the values are fetched from `/config.json` -- a small
  file next to the bundle that CD writes at deploy time.

  PRECEDENCE IS PER FIELD. A non-empty value in `config.json` wins; anything empty or
  absent falls through to the build-time value. That lets the file in `public/` ship
  inert, so local development keeps reading `.env.development`.

  NOTHING SECRET GOES IN HERE. It is a public file served to every browser.

  Copied from dataroom's runtimeConfig.ts; adds `canonicalOrigin` and `devOrigins` for
  the one-origin rule (brief §2.6, §8.1).
*/

export interface RuntimeConfig {
  keycloak: {
    /** Base URL, no realm path. Empty runs in stub mode against the API's dev stub. */
    url: string
    realm: string
    /** The public client. Also what the API checks as `azp`. */
    clientId: string
  }
  /** Absolute API origin. Empty means same-origin (the dev proxy). */
  apiBaseUrl: string
  /**
   * The one production origin. A production bundle refuses to run capture screens on
   * any other origin unless it is listed in `devOrigins` (brief §2.6).
   */
  canonicalOrigin: string
  /** Origins where a production bundle may still run: dev, preview, localhost. */
  devOrigins: string[]
}

/** What the bundle was built with. The fallback for anything `config.json` leaves empty. */
const buildTime: RuntimeConfig = {
  keycloak: {
    url: trimTrailingSlash(import.meta.env.VITE_KEYCLOAK_URL ?? ''),
    realm: import.meta.env.VITE_KEYCLOAK_REALM || 'tally4244-dev',
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'tally4244-web',
  },
  apiBaseUrl: trimTrailingSlash(import.meta.env.VITE_API_BASE_URL ?? ''),
  canonicalOrigin: trimTrailingSlash(import.meta.env.VITE_CANONICAL_ORIGIN ?? ''),
  devOrigins: ['http://localhost:5174', 'http://localhost:4173'],
}

let resolved: RuntimeConfig = buildTime

/**
 * Loads `/config.json` and merges it over the build-time values. Must be awaited before
 * anything reads {@link runtimeConfig}; `main.tsx` does it before Keycloak is built.
 *
 * A missing or unreadable file is not an error: the build-time values are a complete
 * configuration on their own. Offline, the service worker serves the last copy it
 * fetched (network-first), which is the right answer for an installed app.
 */
export async function loadRuntimeConfig(fetchImpl: typeof fetch = fetch): Promise<RuntimeConfig> {
  try {
    // no-store: this file is how an operator repoints a deployed SPA, so a stale copy
    // is the app still talking to the previous identity provider.
    const response = await fetchImpl('/config.json', { cache: 'no-store' })

    if (!response.ok) {
      return resolved
    }

    // The SPA's navigation fallback rewrites unknown paths to index.html, so a
    // misconfigured host answers this fetch with 200 and a page of HTML. Check before
    // parsing rather than let JSON.parse report "Unexpected token '<'".
    const contentType = response.headers.get('content-type') ?? ''

    if (!contentType.includes('json')) {
      console.warn(
        '/config.json did not come back as JSON. The host is probably serving index.html ' +
          'for it. Using the values built into the bundle.',
      )
      return resolved
    }

    const file = (await response.json()) as Partial<RuntimeConfig> | null

    resolved = {
      keycloak: {
        url: trimTrailingSlash(pick(file?.keycloak?.url, buildTime.keycloak.url)),
        realm: pick(file?.keycloak?.realm, buildTime.keycloak.realm),
        clientId: pick(file?.keycloak?.clientId, buildTime.keycloak.clientId),
      },
      apiBaseUrl: trimTrailingSlash(pick(file?.apiBaseUrl, buildTime.apiBaseUrl)),
      canonicalOrigin: trimTrailingSlash(pick(file?.canonicalOrigin, buildTime.canonicalOrigin)),
      devOrigins: pickList(file?.devOrigins, buildTime.devOrigins),
    }
  } catch {
    // Network failure, malformed JSON, or no file at all: use the build-time values.
  }

  return resolved
}

/** The resolved configuration. Returns the build-time values until the load completes. */
export function runtimeConfig(): RuntimeConfig {
  return resolved
}

/** Test seam: forget any loaded file. */
export function resetRuntimeConfigForTests(): void {
  resolved = buildTime
}

/**
 * Whether this page is somewhere a production bundle may capture field data.
 *
 * Only the production bundle enforces it: a dev build on localhost is the normal case
 * for a developer, and the dev server's origin is in the list anyway. An empty
 * canonical origin disables the check, which is how a preview deployment with no
 * `config.json` keeps working.
 */
export function isAllowedOrigin(origin: string = window.location.origin): boolean {
  if (import.meta.env.MODE !== 'production') {
    return true
  }
  const { canonicalOrigin, devOrigins } = resolved
  if (!canonicalOrigin) {
    return true
  }
  return origin === canonicalOrigin || devOrigins.includes(origin)
}

function pick(value: string | undefined | null, fallback: string): string {
  const trimmed = typeof value === 'string' ? value.trim() : ''
  return trimmed || fallback
}

function pickList(value: unknown, fallback: string[]): string[] {
  if (!Array.isArray(value)) return fallback
  const items = value
    .filter((v): v is string => typeof v === 'string')
    .map((v) => trimTrailingSlash(v.trim()))
    .filter(Boolean)
  return items.length > 0 ? items : fallback
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '')
}
